/**
 * ConversationDrawer - Compact conversation list
 *
 * Renders pinned/regular/archived conversation items in a single-line compact format.
 * Groups regular conversations by date: "Hoy", "Ayer", "Esta semana", "Este mes", "Anteriores".
 * Shows relative timestamps per conversation ("hace 2h", "Ayer", etc.).
 * No search bar, no footer — the parent (UnifiedSidebar) provides those.
 * Receives searchQuery as a prop for filtering.
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Pin, Archive, ArchiveRestore, Pencil, MoreVertical, Clock } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import SectionHeader from './sidebar/SectionHeader';
import type { Conversation } from '../services/api';
import type { ConversationUpdateRequest } from '../services/api/types/conversation';

// ─── Date helpers ────────────────────────────────────────────────

/** Returns the best date for a conversation (last_message_at > updated_at > created_at) */
function getConversationDate(conv: Conversation): Date {
  const dateStr = conv.last_message_at || conv.updated_at || conv.created_at;
  return new Date(dateStr);
}

/** Formats a relative time string in Spanish */
function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHrs = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMin < 1) return 'ahora';
  if (diffMin < 60) return `hace ${diffMin}m`;
  if (diffHrs < 24) return `hace ${diffHrs}h`;
  if (diffDays === 1) return 'Ayer';
  if (diffDays < 7) return `hace ${diffDays}d`;

  // Older: show short date
  return date.toLocaleDateString('es-CL', { day: 'numeric', month: 'short' });
}

type DateGroup = 'hoy' | 'ayer' | 'semana' | 'mes' | 'anteriores';

const DATE_GROUP_LABELS: Record<DateGroup, string> = {
  hoy: 'Hoy',
  ayer: 'Ayer',
  semana: 'Esta semana',
  mes: 'Este mes',
  anteriores: 'Anteriores',
};

/** Assigns a date group to a conversation */
function getDateGroup(date: Date): DateGroup {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86_400_000);
  const weekAgo = new Date(today.getTime() - 7 * 86_400_000);
  const monthAgo = new Date(today.getTime() - 30 * 86_400_000);

  if (date >= today) return 'hoy';
  if (date >= yesterday) return 'ayer';
  if (date >= weekAgo) return 'semana';
  if (date >= monthAgo) return 'mes';
  return 'anteriores';
}

/** Groups conversations by date, preserving order within each group */
function groupByDate(convs: Conversation[]): Array<{ group: DateGroup; items: Conversation[] }> {
  const groups = new Map<DateGroup, Conversation[]>();
  const order: DateGroup[] = ['hoy', 'ayer', 'semana', 'mes', 'anteriores'];

  for (const conv of convs) {
    const group = getDateGroup(getConversationDate(conv));
    if (!groups.has(group)) groups.set(group, []);
    groups.get(group)!.push(conv);
  }

  return order.filter((g) => groups.has(g)).map((g) => ({ group: g, items: groups.get(g)! }));
}

interface ConversationDrawerProps {
  conversations: Conversation[];
  currentConversation: Conversation | null;
  onSelect: (conversation: Conversation) => void;
  onCreate: (name: string) => Promise<any>;
  onUpdate: (id: string, updates: ConversationUpdateRequest) => Promise<boolean>;
  /** Search query from parent (UnifiedSidebar) */
  searchQuery?: string;
}

const ConversationDrawer: React.FC<ConversationDrawerProps> = ({
  conversations,
  currentConversation,
  onSelect,
  onCreate,
  onUpdate,
  searchQuery = '',
}) => {
  const { theme } = useTheme();
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const STORAGE_KEY = 'mychat-chat-collapsed-groups';

  const [showArchived, setShowArchived] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const groups: string[] = JSON.parse(saved);
        return !groups.includes('archivadas');
      }
    } catch (_e) {
      // Ignore localStorage parse errors
    }
    return false;
  });

  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) return new Set(JSON.parse(saved).filter((g: string) => g !== 'archivadas'));
    } catch (_e) {
      // Ignore localStorage parse errors
    }
    return new Set();
  });

  // Persist collapsed state to localStorage
  useEffect(() => {
    const groups = [...collapsedGroups];
    if (!showArchived) groups.push('archivadas');
    localStorage.setItem(STORAGE_KEY, JSON.stringify(groups));
  }, [collapsedGroups, showArchived]);

  const toggleGroup = useCallback((group: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  }, []);
  const menuButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const menuRef = useRef<HTMLDivElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);

  // Escape to close menus/edits
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (menuOpenId) {
          setMenuOpenId(null);
        } else if (editingId) {
          setEditingId(null);
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [menuOpenId, editingId]);

  // Close context menu on outside click
  useEffect(() => {
    if (!menuOpenId) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current?.contains(e.target as Node)) return;
      setMenuOpenId(null);
    };
    const timer = setTimeout(() => {
      document.addEventListener('click', handleClick);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClick);
    };
  }, [menuOpenId]);

  // Focus edit input
  useEffect(() => {
    if (editingId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingId]);

  // Filter conversations by search query
  const filteredConversations = useMemo(() => {
    if (!searchQuery.trim()) return conversations;
    const q = searchQuery.toLowerCase();
    return conversations.filter((c) => c.name.toLowerCase().includes(q));
  }, [conversations, searchQuery]);

  // Split into pinned, regular, and archived
  const { pinned, regular, archived } = useMemo(() => {
    const p: Conversation[] = [];
    const r: Conversation[] = [];
    const a: Conversation[] = [];
    for (const c of filteredConversations) {
      if (c.is_active === false) a.push(c);
      else if (c.is_pinned) p.push(c);
      else r.push(c);
    }
    return { pinned: p, regular: r, archived: a };
  }, [filteredConversations]);

  const activeConversations = useMemo(() => [...pinned, ...regular], [pinned, regular]);

  const handleStartRename = useCallback((conv: Conversation) => {
    setEditingId(conv.id);
    setEditName(conv.name);
    setMenuOpenId(null);
  }, []);

  const handleSaveRename = useCallback(
    async (id: string) => {
      const trimmed = editName.trim();
      if (trimmed && trimmed !== conversations.find((c) => c.id === id)?.name) {
        await onUpdate(id, { name: trimmed });
      }
      setEditingId(null);
    },
    [editName, conversations, onUpdate]
  );

  const handlePin = useCallback(
    async (conv: Conversation) => {
      await onUpdate(conv.id, { is_pinned: !conv.is_pinned });
      setMenuOpenId(null);
    },
    [onUpdate]
  );

  const handleArchive = useCallback(
    async (id: string) => {
      await onUpdate(id, { is_active: false });
      setMenuOpenId(null);
    },
    [onUpdate]
  );

  const handleRestore = useCallback(
    async (id: string) => {
      await onUpdate(id, { is_active: true });
      setMenuOpenId(null);
    },
    [onUpdate]
  );

  // ─── Portal menu positioning ────────────────────────────────────
  const getMenuPosition = useCallback((convId: string): React.CSSProperties => {
    const button = menuButtonRefs.current[convId];
    if (!button) return { top: 0, left: 0 };

    const rect = button.getBoundingClientRect();
    const menuWidth = 176;
    const estimatedMenuHeight = 140;

    let left = rect.right - menuWidth;
    left = Math.max(8, Math.min(left, window.innerWidth - menuWidth - 8));

    const spaceBelow = window.innerHeight - rect.bottom;
    const top =
      spaceBelow < estimatedMenuHeight && rect.top > estimatedMenuHeight
        ? rect.top - estimatedMenuHeight
        : rect.bottom + 4;

    return { top, left };
  }, []);

  const renderConversationItem = (conv: Conversation, isArchived = false) => {
    const isActive = currentConversation?.id === conv.id;
    const isEditing = editingId === conv.id;

    return (
      <div
        key={conv.id}
        className={`group/conv flex items-center gap-2 px-3 py-1 cursor-pointer transition-all duration-150 hover:${
          theme.colors.bg.secondary
        } ${isActive ? `${theme.colors.bg.secondary}` : ''}`}
        style={
          isActive
            ? {
                boxShadow: 'inset 2px 0 0 0 var(--nb-accent)',
                background: 'color-mix(in srgb, var(--nb-accent) 8%, transparent)',
              }
            : undefined
        }
        onClick={() => {
          if (!isEditing && !menuOpenId) {
            onSelect(conv);
          }
        }}
        onDoubleClick={(e) => {
          e.stopPropagation();
          if (!isEditing && !isArchived) {
            handleStartRename(conv);
          }
        }}
      >
        {/* Name (truncates to fill available space) */}
        {isEditing ? (
          <input
            ref={editInputRef}
            type="text"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSaveRename(conv.id);
              if (e.key === 'Escape') setEditingId(null);
            }}
            onBlur={() => handleSaveRename(conv.id)}
            onClick={(e) => e.stopPropagation()}
            className={`flex-1 min-w-0 text-xs px-1.5 py-0.5 rounded border ${theme.colors.border.focus} ${theme.colors.bg.input} ${theme.colors.text.primary} outline-none`}
          />
        ) : (
          <div className="flex-1 min-w-0 flex items-center gap-1">
            {conv.is_pinned && !isArchived && (
              <Pin className="w-2.5 h-2.5 flex-shrink-0 text-nb-accent" />
            )}
            <span
              className={`text-xs font-medium truncate ${
                isArchived ? theme.colors.text.muted : theme.colors.text.primary
              }`}
            >
              {conv.name}
            </span>
          </div>
        )}

        {/* Right column: timestamp (hides on hover) / ⋮ menu (shows on hover) */}
        {!isEditing && (
          <>
            <span
              className={`flex-shrink-0 text-xs tabular-nums ${theme.colors.text.secondary} group-hover/conv:hidden`}
            >
              {formatRelativeTime(getConversationDate(conv))}
            </span>
            <button
              ref={(el) => {
                menuButtonRefs.current[conv.id] = el;
              }}
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpenId(menuOpenId === conv.id ? null : conv.id);
              }}
              aria-label="Opciones de conversacion"
              className={`flex-shrink-0 w-6 h-6 items-center justify-center rounded hidden group-hover/conv:flex ${theme.colors.text.muted} ${theme.colors.state.hover}`}
            >
              <MoreVertical className="w-3.5 h-3.5" />
            </button>
          </>
        )}
      </div>
    );
  };

  // Find the conversation for the open menu (search in all lists)
  const allConvs = useMemo(
    () => [...activeConversations, ...archived],
    [activeConversations, archived]
  );
  const menuConv = menuOpenId ? allConvs.find((c) => c.id === menuOpenId) : null;
  const isMenuConvArchived = menuConv?.is_active === false;

  // Group regular (non-pinned) conversations by date
  const dateGroups = useMemo(() => groupByDate(regular), [regular]);

  return (
    <div>
      {activeConversations.length === 0 && archived.length === 0 ? (
        <div className={`text-center py-4 text-xs ${theme.colors.text.muted}`}>
          {searchQuery ? 'Sin resultados' : 'Sin conversaciones'}
        </div>
      ) : (
        <>
          {/* Pinned conversations (no date grouping) */}
          {pinned.length > 0 && (
            <>
              {pinned.map((c) => renderConversationItem(c))}
              {/* pt-3 on first date group header provides visual separation */}
            </>
          )}

          {/* Regular conversations grouped by date */}
          {dateGroups.map(({ group, items }) => (
            <React.Fragment key={group}>
              <SectionHeader
                label={DATE_GROUP_LABELS[group]}
                count={items.length}
                icon={Clock}
                hideIcon
                collapsed={collapsedGroups.has(group)}
                onToggle={() => toggleGroup(group)}
                theme={theme}
              />
              {!collapsedGroups.has(group) && items.map((c) => renderConversationItem(c))}
            </React.Fragment>
          ))}

          {/* Archived section */}
          {archived.length > 0 && (
            <>
              <SectionHeader
                label="Archivadas"
                count={archived.length}
                icon={Archive}
                hideIcon
                collapsed={!showArchived}
                onToggle={() => setShowArchived(!showArchived)}
                theme={theme}
              />
              {showArchived && archived.map((c) => renderConversationItem(c, true))}
            </>
          )}
        </>
      )}

      {/* Context menu portal */}
      {menuOpenId &&
        menuConv &&
        createPortal(
          <div
            ref={menuRef}
            className={`fixed w-44 rounded-lg shadow-lg border z-[9999] py-1 ${theme.colors.bg.secondary} ${theme.colors.border.primary}`}
            style={getMenuPosition(menuOpenId)}
            onClick={(e) => e.stopPropagation()}
          >
            {isMenuConvArchived ? (
              /* Archived conversation menu: only Restore */
              <button
                onClick={() => handleRestore(menuConv.id)}
                className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 ${theme.colors.state.hover}`}
              >
                <ArchiveRestore className="w-3.5 h-3.5" /> Restaurar
              </button>
            ) : (
              /* Active conversation menu */
              <>
                <button
                  onClick={() => handleStartRename(menuConv)}
                  className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 ${theme.colors.state.hover}`}
                >
                  <Pencil className="w-3.5 h-3.5" /> Renombrar
                </button>
                <button
                  onClick={() => handlePin(menuConv)}
                  className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 ${theme.colors.state.hover}`}
                >
                  <Pin className="w-3.5 h-3.5" /> {menuConv.is_pinned ? 'Desfijar' : 'Fijar'}
                </button>
                <div className={`my-1 border-t ${theme.colors.border.primary}`} />
                <button
                  onClick={() => handleArchive(menuConv.id)}
                  className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 ${theme.colors.state.hover}`}
                >
                  <Archive className="w-3.5 h-3.5" /> Archivar
                </button>
              </>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

export default ConversationDrawer;
